import PropTypes from 'prop-types';
import { useMemo, useState, useRef } from 'react';
import Select from 'react-select';
import SelectAsync from 'react-select/async';
import SelectAsyncCreatable from 'react-select/async-creatable';
import SelectCreatable from 'react-select/creatable';
import styled, { useTheme } from 'styled-components';

import {
  calculateSelectMenuPlacement,
  getReactSelectCustomComponents,
  getReactSelectCustomStyles,
} from '@hivebrite/design-system/Inputs';
import { useBreakpoint } from '@hivebrite/design-system/Theme';

import {
  getSelectedOptions,
  getOptionLabel,
  getOptionValue,
  formatOptions,
} from '../utils/selectValues';

const getComponent = ({ async, creatable }) => {
  if (async) {
    if (creatable) {
      return SelectAsyncCreatable;
    }

    return SelectAsync;
  }

  if (creatable) {
    return SelectCreatable;
  }

  return Select;
};

/**
* @deprecated but since alumni_connect ReactSelect
handle much more options format than @hivebrite/design-system/Inputs
we will need to use this one; waiting a unification of backend API.
*/
const ReactSelect = (props) => {
  const {
    ariaDescribedBy = '',
    ariaLabel = '',
    async = false,
    components = {},
    creatable = false,
    defaultValue,
    disabled,
    id,
    isClearable = false,
    isNotSearchable = false,
    isLoading = false,
    maxMenuHeight,
    menuPortalTarget,
    multiple = false,
    name,
    noOptionsMessage = () => <NoOptionsMessage />,
    notCloseMenuOnSelect = false,
    onChange,
    options = [],
    placeholder = I18n.t('select_an_option'),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    showValueAsPlaceholder = false,
    value,
    variant = 'normal',
    autoComplete,
    menuShouldBlockScroll = false,
    ...otherProps
  } = props;

  const theme = useTheme();
  const themeInput = theme.input[variant];
  const breakpoint = useBreakpoint();
  const formattedOptions = formatOptions({ options });
  const isControlled = value !== undefined;
  const selectedOptions = async
    ? value
    : getSelectedOptions({
        selectedValue: value,
        isControlled,
        options: formattedOptions,
        multiple,
        creatable,
      });
  const selectedDefaultOptions = async
    ? defaultValue
    : getSelectedOptions({
        selectedValue: defaultValue,
        isControlled,
        options: formattedOptions,
        multiple,
        creatable,
      });

  const Component = getComponent({ async, creatable });

  const customStyles = getReactSelectCustomStyles({
    theme,
    themeInput,
    breakpoint,
  });

  const describedById = `${id}-describedBy`;
  const customComponents = useMemo(
    () => getReactSelectCustomComponents({ theme, themeInput }),
    [theme, themeInput],
  );

  const selectElement = useRef();
  const [menuPlacement, setMenuPlacement] = useState('auto');

  const calculateMenuPlacement = () => {
    const recalculatedMenuPlacement = calculateSelectMenuPlacement({
      ref: selectElement,
      menuSize: theme.dropdown.maxHeight,
      options: formattedOptions,
    });

    if (recalculatedMenuPlacement !== menuPlacement) {
      setMenuPlacement(recalculatedMenuPlacement);
    }
  };

  return (
    <div ref={selectElement}>
      <Component
        aria-describedby={ariaDescribedBy}
        aria-label={ariaLabel}
        closeMenuOnSelect={!notCloseMenuOnSelect}
        components={{ ...customComponents, ...components }}
        defaultValue={selectedDefaultOptions}
        describedById={describedById}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        inputId={id}
        isClearable={isClearable}
        isDisabled={disabled}
        isMulti={multiple}
        isSearchable={!isNotSearchable}
        maxMenuHeight={maxMenuHeight}
        menuPortalTarget={menuPortalTarget || document.body}
        name={name}
        noOptionsMessage={noOptionsMessage}
        onChange={onChange}
        onMenuOpen={calculateMenuPlacement}
        options={formattedOptions}
        placeholder={placeholder}
        styles={customStyles}
        value={selectedOptions}
        menuShouldScrollIntoView={false}
        autoComplete={autoComplete}
        isLoading={isLoading}
        menuPlacement={menuPlacement}
        menuShouldBlockScroll={menuShouldBlockScroll}
        {...otherProps}
      />
      {ariaDescribedBy && (
        <p className="accessibility" id={describedById}>
          {ariaDescribedBy}
        </p>
      )}
    </div>
  );
};

ReactSelect.propTypes = {
  async: PropTypes.bool,
  creatable: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  showValueAsPlaceholder: PropTypes.bool,
  // Passing a value will make react-select controlled
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
    PropTypes.object,
  ]),
  defaultValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
    PropTypes.object,
  ]),
  onChange: PropTypes.func,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string,
  // Accepted options format:
  //  ['AA', 'BB']
  //  [{ label/name/text/title: 'My text 1', value/id: 'my_value_1' }, { label/name/text/title: 'My text 2', value/id: 'my_value_2' }]
  //  { my_value_1: 'My text 1', my_value_2: 'My text 2' }
  options: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  multiple: PropTypes.bool,
  isClearable: PropTypes.bool,
  isNotSearchable: PropTypes.bool,
  notCloseMenuOnSelect: PropTypes.bool,
  noOptionsMessage: PropTypes.func,
  maxMenuHeight: PropTypes.number,
  ariaLabel: PropTypes.string,
  ariaDescribedBy: PropTypes.string,
  variant: PropTypes.oneOf(['normal', 'small']),
  menuShouldBlockScroll: PropTypes.bool,
};

export default ReactSelect;

const NoOptionsMessageSpan = styled.span`
  color: ${(props) => props.theme.colors.dove} !important;
`;

const NoOptionsMessage = () => (
  <NoOptionsMessageSpan>{__('No options')}</NoOptionsMessageSpan>
);
